import React, { useState, useMemo } from 'react';
import { I18n } from '@kineticdata/react';
import { InfoMessage, EmptyMessage } from '@kineticdata/bundle-common';
import classNames from 'classnames';
import { Map } from 'immutable';

const ResultGroup = ({ parentId, ...props }) => {
  if (!props.results) {
    return (
      <InfoMessage message="Enter a search query to find matching content." />
    );
  } else if (props.results.size === 0) {
    return (
      <EmptyMessage message="Your search query could not be found in any of the sections." />
    );
  }

  // Filter to the correct subset of items based on parentId
  const contentList = props.content.filter(
    item =>
      !item.settings &&
      (parentId ? item.parentId === parentId : !item.parentId) &&
      !!(props.results.get(item.id) || [])[0],
  );

  return contentList && contentList.size > 0 ? (
    <ul className={classNames('list-unstyled', { 'pl-3': !!parentId })}>
      {contentList
        // Map each item
        .map(item => {
          const matchCount = props.results.get(item.id)[1] || 0;
          const sectionIndex =
            !!props.contentIndexMap && props.contentIndexMap.get(item.id);
          return (
            <li key={item.id}>
              <button
                className="btn btn-link mb-1 text-left"
                onClick={props.onClick(item.id)}
                disabled={matchCount <= 0}
              >
                <span className="label">
                  {sectionIndex && (
                    <small className="text-muted mr-2">{sectionIndex}</small>
                  )}
                  {item.title ? (
                    <I18n>{item.title}</I18n>
                  ) : (
                    <em>
                      <I18n>Missing Title</I18n>
                    </em>
                  )}
                </span>
                {matchCount > 0 && (
                  <small className="text-muted ml-2 text-nowrap">
                    ({matchCount}{' '}
                    <I18n>{`match${matchCount > 1 ? 's' : ''}`}</I18n>)
                  </small>
                )}
              </button>
              <ResultGroup
                key={`children-${item.id}`}
                {...props}
                parentId={item.id}
              />
            </li>
          );
        })
        .toList()}
    </ul>
  ) : null;
};

const SearchRenderer = ({ content, handleActiveIdUpdate, contentIndexMap }) => {
  const [q, setQ] = useState('');

  const results = useMemo(
    () => {
      if (!q || !q.trim()) {
        return null;
      } else {
        return content.filter(item => !item.settings).reduce((map, item) => {
          const query = new RegExp(
            q.trim().replace(/[|\\{}()[\]^$+*?.]/g, '\\$&'),
            'gi',
          );
          const titleMatch = item.title.match(query);
          const valueMatch = item.value.match(query);
          const matchCount =
            (titleMatch || []).length + (valueMatch || []).length;
          if (matchCount > 0) {
            return item.ancestorIds.reduce(
              (m, id) =>
                m.update(id, v => (Array.isArray(v) ? [true, v[1]] : [true])),
              map.set(item.id, [true, matchCount]),
            );
          } else {
            return map;
          }
        }, Map());
      }
    },
    [content, q],
  );

  return (
    <>
      <h2>
        <I18n>Search</I18n>
      </h2>

      <div className="form form-unstyled">
        <div className="form-group">
          <input
            type="text"
            id="search-valuename"
            aria-label="Search Criteria"
            placeholder="What would you like to search for?"
            value={q}
            onChange={e => setQ(e.target.value || '')}
            autoFocus
          />
        </div>

        <ResultGroup
          content={content}
          results={results}
          onClick={handleActiveIdUpdate}
          contentIndexMap={contentIndexMap}
        />
      </div>
    </>
  );
};

// ID of secton using this renderer
SearchRenderer.ID = 'SEARCH';
SearchRenderer.HIDDEN = true;

export default SearchRenderer;
